import React, { useState } from 'react'
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { TrashIcon } from '@radix-ui/react-icons'
import { v4 as uuid } from 'uuid'

import { RemoveAttendeeButton } from '@/components/Event/attendee/AttendeeControls'
import {
  emailValidation,
  nameValidation,
} from '@/components/Event/Feedback/validation'
import { StateSelect } from '@/components/StateSelect'
import { ATTENDEE_ROLE_LABEL, AttendeeRole } from '@/constants'
import { AttendeeFormValues, ZoomParticipant } from '@/types'
import { STATES_CODES } from '@/utils/states'

// The form always assigns attendees a uuid, so the id is required here rather than cast
// at every use.
export type MappableAttendee = AttendeeFormValues & { id: string }

const WITNESS_WARNING = 'This new witness will be added to the event.'
const WITNESS_BADGE = 'New witness'

const AttendeeRow: React.FC<{
  children: React.ReactNode
}> = ({ children }) => (
  <Box
    bg="indigo.1"
    p="md"
    sx={theme => ({
      borderRadius: theme.radius.sm,
      width: '100%',
    })}
  >
    {children}
  </Box>
)

// Body of the late-joiner mapping modal: pick the people who joined under one Zoom
// account. Lists every attendee, unlike DR, which hides those marked as not having
// joined — otherwise they could never be mapped. Attendees added here stay local until
// Confirm, so Cancel and remove discard them without touching the form.
export const ParticipantMappingModal: React.FC<{
  participant: ZoomParticipant
  attendees: MappableAttendee[]
  // Everyone on the event, across all witness sections. Only used to reject duplicates:
  // Confirm appends to every section, so a name already used in another one still
  // collides even though it isn't listed here.
  allEventAttendees: AttendeeFormValues[]
  // The caller drops Witness when no further witness section can be appended.
  roles: AttendeeRole[]
  witnessLocationEnabled?: boolean
  onConfirm: (payload: {
    attendeeIds: string[]
    newAttendees: MappableAttendee[]
  }) => void
}> = ({
  participant,
  attendees,
  allEventAttendees,
  roles,
  witnessLocationEnabled = false,
  onConfirm,
}) => {
  const [selectedAttendeeIds, setSelectedAttendeeIds] = useState<string[]>([])
  const [newAttendees, setNewAttendees] = useState<MappableAttendee[]>([])
  const [formOpen, setFormOpen] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<AttendeeRole | null>(null)
  const [city, setCity] = useState('')
  const [state, setState] = useState<string | null>(null)

  const isWitness = role === AttendeeRole.WITNESS
  const showWitnessLocation = isWitness && witnessLocationEnabled

  // The Attendance step's own rules, so the modal cannot create a row that fails later.
  const listed = [...allEventAttendees, ...newAttendees]
  const normalize = (value: string | null | undefined) =>
    value?.trim().toLowerCase() ?? ''
  // A saved draft keys attendee ids by name, so two rows sharing a name end up sharing
  // an id — and buildEventFeedbackPayload keys participants_mapping by name too.
  const isDuplicateName =
    !!name.trim() &&
    listed.some(attendee => normalize(attendee.name) === normalize(name))
  const nameError = name
    ? nameValidation(name) ??
      (isDuplicateName ? 'Attendee with this name is already listed' : null)
    : null
  const isDuplicateEmail =
    !!email.trim() &&
    listed.some(attendee => normalize(attendee.email) === normalize(email))
  const emailError = isDuplicateEmail
    ? 'Email is already in use'
    : emailValidation(email) ?? null
  const missingWitnessLocation = showWitnessLocation && (!city.trim() || !state)
  const canAdd =
    !!name.trim() &&
    !!role &&
    !nameError &&
    !emailError &&
    !missingWitnessLocation

  const closeForm = () => {
    setFormOpen(false)
    setName('')
    setEmail('')
    setRole(null)
    setCity('')
    setState(null)
  }

  const addAndSelect = () => {
    if (!canAdd || !role) {
      return
    }

    const id = uuid()

    setNewAttendees(current => [
      ...current,
      {
        id,
        name: name.trim(),
        email: email.trim() || null,
        role,
        // Confirm flips this to Yes along with the mapping.
        present: null,
        zoom_participants: [],
        ...(showWitnessLocation
          ? { city: city.trim() || null, state }
          : undefined),
      },
    ])
    // Untickable: the attendee is still created, just not mapped to this account.
    setSelectedAttendeeIds(current => [...current, id])
    closeForm()
  }

  const removeNewAttendee = (id: string) => {
    setNewAttendees(current => current.filter(attendee => attendee.id !== id))
    setSelectedAttendeeIds(current =>
      current.filter(attendeeId => attendeeId !== id),
    )
  }

  return (
    <Stack spacing="md">
      <Text fw={600} mb="sm" size="sm">
        Select all people joining under this &quot;{participant.user_name}&quot;
        Zoom account.
      </Text>
      <Stack spacing="xs" align="flex-start">
        <Checkbox.Group
          value={selectedAttendeeIds}
          onChange={setSelectedAttendeeIds}
          w="100%"
        >
          <Stack spacing="xs">
            {attendees.map(attendee => (
              <AttendeeRow key={attendee.id}>
                <Group>
                  <Checkbox
                    styles={{ input: { cursor: 'pointer' } }}
                    value={attendee.id}
                  />
                  <Stack spacing={0}>
                    <Text fw={600}>{attendee.name}</Text>
                    <Text>
                      {ATTENDEE_ROLE_LABEL[attendee.role] || 'Unknown role'}
                    </Text>
                  </Stack>
                </Group>
              </AttendeeRow>
            ))}

            {/* Added rows behave like the rest, plus a remove button. */}
            {newAttendees.map(attendee => (
              <AttendeeRow key={attendee.id}>
                <Group position="apart" noWrap align="flex-start">
                  <Group>
                    <Checkbox
                      styles={{ input: { cursor: 'pointer' } }}
                      value={attendee.id}
                    />
                    <Stack spacing={0}>
                      <Text fw={600}>{attendee.name}</Text>
                      <Group spacing="xs">
                        <Text>
                          {ATTENDEE_ROLE_LABEL[attendee.role] || 'Unknown role'}
                        </Text>
                        {attendee.role === AttendeeRole.WITNESS && (
                          <Badge color="orange" variant="light">
                            {WITNESS_BADGE}
                          </Badge>
                        )}
                      </Group>
                    </Stack>
                  </Group>
                  <RemoveAttendeeButton
                    onClick={() => removeNewAttendee(attendee.id)}
                  >
                    <TrashIcon width={20} height={20} />
                  </RemoveAttendeeButton>
                </Group>
              </AttendeeRow>
            ))}
          </Stack>
        </Checkbox.Group>

        {formOpen ? (
          <AttendeeRow>
            <Stack spacing="sm">
              {/* An Attendance step row, minus Joined Zoom and the Zoom mapping. */}
              <Group grow noWrap align="flex-start">
                <TextInput
                  autoFocus
                  required
                  label="Name"
                  value={name}
                  error={nameError}
                  onChange={event => setName(event.target.value)}
                />
                <TextInput
                  label="Email"
                  type="email"
                  value={email}
                  error={emailError}
                  onChange={event => setEmail(event.target.value)}
                />
              </Group>
              <Group grow noWrap align="flex-start">
                <Select
                  required
                  styles={{ item: { wordBreak: 'normal' } }}
                  label="Role"
                  placeholder="Pick one"
                  data={roles.map(attendeeRole => ({
                    value: attendeeRole,
                    label: ATTENDEE_ROLE_LABEL[attendeeRole],
                  }))}
                  value={role}
                  onChange={value => setRole(value as AttendeeRole | null)}
                />
                {/* Keeps Role the same width as Name above. */}
                <Box />
              </Group>
              {showWitnessLocation && (
                <Group grow noWrap align="flex-start">
                  <TextInput
                    required
                    label="City"
                    value={city}
                    onChange={event => setCity(event.target.value)}
                  />
                  <StateSelect
                    required
                    label="State"
                    placeholder="Pick one"
                    states={STATES_CODES}
                    value={state}
                    onChange={setState}
                  />
                </Group>
              )}
              {isWitness && (
                <Alert color="orange" variant="light">
                  {WITNESS_WARNING}
                </Alert>
              )}
              <Group spacing="sm" position="right">
                <Button type="button" variant="default" onClick={closeForm}>
                  Cancel
                </Button>
                <Button type="button" disabled={!canAdd} onClick={addAndSelect}>
                  Add and select
                </Button>
              </Group>
            </Stack>
          </AttendeeRow>
        ) : (
          <Button
            type="button"
            variant="subtle"
            onClick={() => setFormOpen(true)}
          >
            Add new attendee
          </Button>
        )}
      </Stack>
      {/* Hidden while adding, so confirming cannot discard what was typed. */}
      {!formOpen && (
        <Stack align="flex-end">
          <Button
            type="button"
            disabled={!selectedAttendeeIds.length}
            onClick={() =>
              onConfirm({ attendeeIds: selectedAttendeeIds, newAttendees })
            }
          >
            Confirm
          </Button>
        </Stack>
      )}
    </Stack>
  )
}
